import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, Button, Input } from '@fyos/components';
import type { DialogType } from '@fyos/components';

interface BaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  loading?: boolean;
  onCancel?: () => void;
}

interface ConfirmDialogProps extends BaseDialogProps {
  type?: 'confirm' | 'alert';
  onConfirm: () => void;
  promptPlaceholder?: never;
  promptDefaultValue?: never;
  onPromptConfirm?: never;
}

interface PromptDialogProps extends BaseDialogProps {
  type: 'prompt';
  onConfirm?: never;
  promptPlaceholder?: string;
  promptDefaultValue?: string;
  onPromptConfirm: (value: string) => void;
}

type AppDialogProps = ConfirmDialogProps | PromptDialogProps;

export default function ConfirmDialog(props: AppDialogProps) {
  const {
    open,
    onOpenChange,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'danger',
    loading = false,
    onCancel,
    type = 'confirm',
  } = props;

  const [promptValue, setPromptValue] = useState(props.type === 'prompt' ? (props.promptDefaultValue || '') : '');

  useEffect(() => {
    if (open && props.type === 'prompt') {
      setPromptValue(props.promptDefaultValue || '');
    }
  }, [open]);

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (props.type === 'prompt') {
      props.onPromptConfirm(promptValue);
    } else {
      props.onConfirm();
    }
  };

  const dialogType: DialogType = type === 'alert' ? 'alert' : type === 'prompt' ? 'prompt' : 'confirm';

  return (
    <Dialog open={open} onOpenChange={onOpenChange} type={dialogType} loading={loading}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {props.type === 'prompt' && (
          <div className="py-2">
            <Input
              value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              placeholder={props.promptPlaceholder}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
              autoFocus
            />
          </div>
        )}
        <DialogFooter>
          {type !== 'alert' && (
            <Button variant="outline" onClick={handleCancel} disabled={loading}>
              {cancelLabel}
            </Button>
          )}
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            disabled={loading || (props.type === 'prompt' && !promptValue.trim())}
          >
            {loading ? 'Please wait...' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
